import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import {
  IoAlertCircleSharp,
  IoEyeOutline,
  IoEyeOffOutline,
} from "react-icons/io5";
import LoadingComponent from "../Components/LoadingComponent";
import useAuth from "../useAuth";
import { Divider, Paper } from "@mui/material";
import { motion } from "framer-motion";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { user, errorMessage, loading, loginWithEmailAndPassword } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    loginWithEmailAndPassword(email, password);
  };

  if (user) {
    return <Navigate to="/my/home" />;
  }

  return (
    <div className="h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-200">
      <motion.div
        initial={{ y: 40, opacity: 0, scale: 0.95 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <Paper
          elevation={4}
          sx={{ p: 3.5, borderRadius: 4, width: "400px" }}
          className="border border-gray-200"
        >
          <form onSubmit={handleLogin}>
            {/* Title */}
            <div className="flex flex-col items-center gap-1">
              <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight">
                BVM Partner
              </h1>
              <p className="-mt-1 capitalize font-semibold text-gray-500 text-sm">(Project Manager)</p>
              <p className="text-gray-500 mt-2">Sign in to your account</p>
            </div>

            <Divider sx={{ mt: 3 }} />

            {/* Input Fields */}
            <div className="flex flex-col gap-5 mt-6">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Username
                </label>
                <input
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Username"
                  className="h-11 border w-full px-3 border-gray-300 rounded-lg bg-gray-100 focus:bg-white focus:ring-2 focus:ring-blue-500 duration-300 outline-none"
                  required
                />
              </div>

              <div className="relative">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Password
                </label>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="h-11 border w-full px-3 border-gray-300 rounded-lg bg-gray-100 focus:bg-white focus:ring-2 focus:ring-blue-500 duration-300 outline-none"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute top-[37px] right-3 text-gray-500 hover:text-gray-800"
                >
                  {showPassword ? (
                    <IoEyeOffOutline className="text-lg" />
                  ) : (
                    <IoEyeOutline className="text-lg" />
                  )}
                </button>
              </div>

              <div className="flex justify-end text-xs text-blue-600 font-medium cursor-pointer hover:underline">
                Forgot password?
              </div>
            </div>

            {/* Error Message */}
            {errorMessage && (
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.25 }}
                className="flex items-center justify-center text-red-500 text-sm mt-3"
              >
                <IoAlertCircleSharp className="mr-1 text-base" />
                {errorMessage}
              </motion.div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              className="mt-6 w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold tracking-wide transition duration-300"
            >
              Sign In
            </button>
          </form>
        </Paper>
      </motion.div>

      {loading && <LoadingComponent />}
    </div>
  );
}
